import React, { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { Trash, X } from "@phosphor-icons/react";
import "./RemoveCartItemModal.css";

const RemoveCartItemModal = ({ show, productName, busy, error, onClose, onConfirm }) => {
  const dialogRef = useRef(null);
  const cancelRef = useRef(null);
  const busyRef = useRef(busy);
  const titleId = useId();
  const descriptionId = useId();
  busyRef.current = busy;

  useEffect(() => {
    if (!show) return undefined;
    const previousFocus = document.activeElement;
    const previousBodyOverflow = document.body.style.overflow;
    const previousPageOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    cancelRef.current?.focus();
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        if (!busyRef.current) onClose();
      }
      if (event.key === "Tab") {
        const buttons = [...dialogRef.current.querySelectorAll("button:not(:disabled)")];
        const first = buttons[0];
        const last = buttons[buttons.length - 1];
        if (!buttons.includes(document.activeElement) || (event.shiftKey && document.activeElement === first)) {
          event.preventDefault();
          (event.shiftKey ? last : first)?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
        if (!buttons.length) dialogRef.current?.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown, true);
    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousPageOverflow;
      document.removeEventListener("keydown", handleKeyDown, true);
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, [show, onClose]);

  if (!show) return null;

  return createPortal(
    <div className="remove-cart-modal" onMouseDown={(event) => {
      if (event.target === event.currentTarget && !busy) {
        event.preventDefault();
        onClose();
      }
    }}>
      <div className="remove-cart-modal__panel" ref={dialogRef} role="dialog" aria-modal="true"
        aria-labelledby={titleId} aria-describedby={descriptionId} aria-busy={busy} tabIndex={-1}>
        <button type="button" className="remove-cart-modal__close" aria-label="Close remove item confirmation" onClick={onClose} disabled={busy}>
          <X size={20} aria-hidden="true" />
        </button>
        <span className="remove-cart-modal__icon"><Trash size={28} weight="duotone" aria-hidden="true" /></span>
        <h2 id={titleId}>Remove item?</h2>
        <p id={descriptionId}>Are you sure you want to remove <strong>{productName}</strong> from your cart?</p>
        {error && <p className="remove-cart-modal__error" role="alert">{error}</p>}
        <div className="remove-cart-modal__actions">
          <button ref={cancelRef} type="button" className="remove-cart-modal__no" onClick={onClose} disabled={busy}>No</button>
          <button type="button" className="remove-cart-modal__yes" onClick={onConfirm} disabled={busy}>{busy ? "Removing..." : "Yes"}</button>
        </div>
      </div>
    </div>, document.body
  );
};

export default RemoveCartItemModal;
